import React, { useState, useEffect, useRef } from 'react';
import { Collapse } from 'antd';
import { DownOutlined, RightOutlined, LeftOutlined } from '@ant-design/icons';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Label } from 'recharts';

/**
 * ScatterChartComponent for displaying scatter plot data
 * 
 * @param {Object} props
 * @param {Object} props.data - The chart data
 * @param {string} props.title - The title of the accordion
 * @param {string} props.uuid - The UUID of the data object
 * @param {number} props.currentVersion - Current version of the data
 * @param {Object} props.versionInfo - Information about all versions
 * @param {Function} props.onVersionChange - Callback when version is changed
 * @param {Function} props.registerComponent - Function to register for updates
 */
const ScatterChartComponent = ({
  data,
  title = "Company Performance",
  uuid,
  currentVersion = 1,
  versionInfo = {},
  onVersionChange,
  registerComponent
}) => {
  // State to track whether the component is expanded
  const [isExpanded, setIsExpanded] = useState(true);
  
  // Handler for version navigation
  const handleVersionChange = (newVersion) => {
    if (onVersionChange && uuid) {
      onVersionChange(uuid, newVersion);
    }
  };


  //////////  Component data sync from backend ?????????????????
  const [updateCounter, setUpdateCounter] = useState(0);

  // Register this component for updates if the function is provided
  useEffect(() => {
    if (registerComponent && uuid) {
      // Create a refresh function to update this component
      const refreshComponent = () => {
        console.log(`Refreshing Component: ${uuid}`);
        setUpdateCounter(prev => prev + 1);
      };
      
      // Register this component with the provided function
      const unregister = registerComponent(refreshComponent);
      
      // Unregister when the component unmounts
      return unregister;
    }
  }, [registerComponent, uuid]);
  //////////  Component data sync from backend ?????????????????

  
  
  // Extract metadata from data if available
  const chartTitle = data?.metadata?.title || title;
  const chartSubtitle = data?.metadata?.subtitle || '';
  const description = data?.metadata?.description || '';
  
  // Extract axis configuration
  const xAxis = data?.axis?.x || { label: "Revenue ($M)", key: "revenue" };
  const yAxis = data?.axis?.y || { label: "Profit ($M)", key: "profit" };
  
  // Format numbers with commas
  const formatNumber = (number) => {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };
  
  // Generate colors based on a metric
  const generateColor = (value, min, max) => {
    // Use the purple color scheme
    const primaryColor = '#5a00d8';   // Dark purple
    const secondaryColor = '#d1b1ff'; // Light purple
    
    // Normalize the value between 0 and 1
    const normalizedValue = Math.min(Math.max((value - min) / (max - min), 0), 1);
    
    // Convert hex to RGB to interpolate
    const r1 = parseInt(primaryColor.substr(1, 2), 16);
    const g1 = parseInt(primaryColor.substr(3, 2), 16);
    const b1 = parseInt(primaryColor.substr(5, 2), 16);
    
    const r2 = parseInt(secondaryColor.substr(1, 2), 16);
    const g2 = parseInt(secondaryColor.substr(3, 2), 16);
    const b2 = parseInt(secondaryColor.substr(5, 2), 16);
    
    // Interpolate between the two colors
    const r = Math.round(r1 * (1 - normalizedValue) + r2 * normalizedValue);
    const g = Math.round(g1 * (1 - normalizedValue) + g2 * normalizedValue);
    const b = Math.round(b1 * (1 - normalizedValue) + b2 * normalizedValue);
    
    // Convert back to hex
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  };
  
  // Prepare the data and colors
  const scatterData = data?.companies || [];
  
  // Find min and max for color scaling
  const ageValues = scatterData.map(item => item.age);
  const minAge = Math.min(...ageValues);
  const maxAge = Math.max(...ageValues);
  
  // Transform data for scatter chart
  const transformedData = scatterData.map(item => ({
    ...item,
    fill: generateColor(item.age, minAge, maxAge) // Color based on company age
  }));
  
  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div style={{
          backgroundColor: '#fff',
          border: '1px solid #ccc',
          padding: '10px',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <p style={{ margin: 0, fontWeight: 'bold' }}>{data.name}</p>
          <p style={{ margin: 0 }}>{xAxis.label}: ${formatNumber(data[xAxis.key])}M</p>
          <p style={{ margin: 0 }}>{yAxis.label}: ${formatNumber(data[yAxis.key])}M</p>
          <p style={{ margin: 0 }}>Age: {data.age} years</p>
        </div>
      );
    }
    return null;
  };
  
  // Check if version navigation should be displayed
  const showVersionNavigation = versionInfo && 
                               versionInfo.available_versions && 
                               versionInfo.available_versions.length > 1;
  
  return (
    <div style={{ 
      marginBottom: '16px', 
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
      borderRadius: '4px'
    }}>
      <Collapse
        defaultActiveKey={['1']}
        expandIcon={({ isActive }) => {
          setIsExpanded(isActive);
          return isActive ? <DownOutlined /> : <RightOutlined />;
        }}
        items={[
          {
            key: '1',
            label: (
              <div style={{ fontWeight: 'bold', fontSize: '18px' }}>
                {chartTitle}
              </div>
            ),
            children: (
              <div>
                {/* Chart Subtitle */}
                {chartSubtitle && (
                  <div style={{ 
                    color: '#8c8c8c', 
                    marginBottom: '16px',
                    fontSize: '14px'
                  }}>
                    {chartSubtitle}
                  </div>
                )}
                
                {/* Scatter Chart */}
                <div style={{ width: '100%', height: 400 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ScatterChart
                      margin={{
                        top: 20,
                        right: 30,
                        bottom: 40,
                        left: 40,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        type="number" 
                        dataKey={xAxis.key} 
                        name={xAxis.label}
                        domain={['auto', 'auto']}
                        tickFormatter={val => `$${formatNumber(val)}M`}
                      >
                        <Label
                          value={xAxis.label}
                          position="bottom"
                          offset={20}
                          style={{ textAnchor: 'middle', fontWeight: 'bold' }}
                        />
                      </XAxis>
                      <YAxis 
                        type="number" 
                        dataKey={yAxis.key}
                        name={yAxis.label}
                        tickFormatter={val => `$${formatNumber(val)}M`}
                      >
                        <Label
                          value={yAxis.label}
                          position="left"
                          angle={-90}
                          offset={-20}
                          style={{ textAnchor: 'middle', fontWeight: 'bold' }}
                        />
                      </YAxis>
                      <Tooltip content={<CustomTooltip />} />
                      <Legend payload={[
                        {
                          value: 'Color: Darker purple = Older company, Lighter purple = Newer company',
                          type: 'line',
                          color: '#5a00d8'
                        }
                      ]} />
                      <Scatter
                        name="Companies"
                        data={transformedData}
                        fill="#5a00d8"
                      />
                    </ScatterChart>
                  </ResponsiveContainer>
                </div>
                
                {/* Description */}
                {description && (
                  <div style={{ 
                    marginTop: '16px',
                    fontSize: '14px',
                    color: '#8c8c8c'
                  }}>
                    {description}
                  </div>
                )}
                
                {/* Version navigation */}
                {showVersionNavigation && (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '16px', 
                    paddingTop: '16px',
                    borderTop: '1px solid #f0f0f0'
                  }}>
                    <button 
                      size="small" 
                      icon={<LeftOutlined />}
                      disabled={currentVersion <= Math.min(...versionInfo.available_versions)}
                      onClick={() => {
                        const versions = versionInfo.available_versions;
                        const currentIndex = versions.indexOf(currentVersion);
                        if (currentIndex > 0) {
                          handleVersionChange(versions[currentIndex - 1]);
                        }
                      }}
                    >
                      Previous
                    </button>
                    
                    <div>
                      Version {currentVersion} of {versionInfo.total_versions}
                    </div>
                    
                    <button 
                      size="small" 
                      icon={<RightOutlined />}
                      disabled={currentVersion >= Math.max(...versionInfo.available_versions)}
                      onClick={() => {
                        const versions = versionInfo.available_versions;
                        const currentIndex = versions.indexOf(currentVersion);
                        if (currentIndex < versions.length - 1) {
                          handleVersionChange(versions[currentIndex + 1]);
                        }
                      }}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )
          }
        ]}
      />
    </div>
  );
};

export default ScatterChartComponent;